import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import {
  IconButton, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, InputAdornment, Box, Chip, Typography, Collapse,
} from '@mui/material';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import RouteIcon from '@mui/icons-material/Route';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TimelineIcon from '@mui/icons-material/Timeline';
import { StatCard, StatCardGrid } from '../common/components/premium/PremiumDashboard';
import { PremiumBarChart, PremiumAreaChart } from '../common/components/premium/PremiumCharts';
import {
  formatDistance, formatSpeed, formatVolume, formatTime, formatNumericHours,
} from '../common/util/formatter';
import ReportFilter from './components/ReportFilter';
import { useAttributePreference } from '../common/util/preferences';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import ReportsMenu from './components/ReportsMenu';
import ColumnSelect from './components/ColumnSelect';
import usePersistedState from '../common/util/usePersistedState';
import { useCatch, useEffectAsync } from '../reactHelper';
import useReportStyles from './common/useReportStyles';
import MapView from '../map/core/MapView';
import MapRoutePath from '../map/MapRoutePath';
import AddressValue from '../common/components/AddressValue';
import PoiLocationValue from '../common/components/PoiLocationValue';
import TableShimmer from '../common/components/TableShimmer';
import MapMarkers from '../map/MapMarkers';
import MapCamera from '../map/MapCamera';
import MapGeofence from '../map/MapGeofence';
import scheduleReport from './common/scheduleReport';
import MapScale from '../map/MapScale';
import fetchOrThrow from '../common/util/fetchOrThrow';
import downloadReportXlsx from '../common/util/downloadReportXlsx';
import { nativePostMessage } from '../common/components/NativeInterface';
import { sessionActions } from '../store';
import { PrintLayout } from './common/print';

const columnsArray = [
  ['startTime', 'reportStartTime'],
  ['startOdometer', 'reportStartOdometer'],
  ['startAddress', 'reportStartAddress'],
  ['endTime', 'reportEndTime'],
  ['endOdometer', 'reportEndOdometer'],
  ['endAddress', 'reportEndAddress'],
  ['distance', 'sharedDistance'],
  ['averageSpeed', 'reportAverageSpeed'],
  ['maxSpeed', 'reportMaximumSpeed'],
  ['duration', 'reportDuration'],
  ['spentFuel', 'reportSpentFuel'],
  ['fuelCost', 'reportFuelCost'],
  ['driverName', 'sharedDriver'],
];
const columnsMap = new Map(columnsArray);

const TripReportPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { classes } = useReportStyles();
  const t = useTranslation();
  const tokens = usePremiumTokens();

  const devices = useSelector((state) => state.devices.items, shallowEqual);
  const printing = useSelector((state) => state.session.printing ?? false);

  const distanceUnit = useAttributePreference('distanceUnit');
  const speedUnit = useAttributePreference('speedUnit');
  const volumeUnit = useAttributePreference('volumeUnit');
  const [columns, setColumns] = usePersistedState('tripColumns', ['startTime', 'endTime', 'distance', 'averageSpeed']);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [route, setRoute] = useState(null);
  const [hasResults, setHasResults] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [fuelPrice, setFuelPrice] = useState(5.50);
  const [showChart, setShowChart] = useState(true);

  // Processar items para adicionar custo de combustível
  const processedItems = useMemo(() => items.map((item) => {
    const device = devices[item.deviceId];
    let fuelUsed = item.spentFuel || 0;
    let fuelEstimated = false;

    // Se não há dados de combustível do sensor, estimar pelo consumo do dispositivo
    if (fuelUsed === 0 && device?.attributes?.form?.fuelConsumption) {
      const consumptionKmL = parseFloat(device.attributes.form.fuelConsumption);
      if (consumptionKmL > 0) {
        fuelUsed = (item.distance / 1000) / consumptionKmL;
        fuelEstimated = true;
      }
    }

    return {
      ...item,
      fuelUsed,
      fuelEstimated,
      fuelCost: fuelUsed * fuelPrice,
    };
  }), [items, devices, fuelPrice]);

  // Stats totais
  const stats = useMemo(() => {
    if (processedItems.length === 0) return null;
    const totalDistance = processedItems.reduce((acc, item) => acc + item.distance, 0);
    const totalFuel = processedItems.reduce((acc, item) => acc + item.fuelUsed, 0);
    const totalFuelCost = processedItems.reduce((acc, item) => acc + item.fuelCost, 0);
    const totalDuration = processedItems.reduce((acc, item) => acc + (item.duration || 0), 0);
    const avgSpeed = processedItems.reduce((acc, item) => acc + (item.averageSpeed || 0), 0) / processedItems.length;
    return {
      trips: processedItems.length,
      totalDistance: (totalDistance / 1000).toFixed(1),
      totalFuel: totalFuel.toFixed(2),
      totalFuelCost: totalFuelCost.toFixed(2),
      totalDuration: (totalDuration / 3600000).toFixed(1), // hours
      avgSpeed: (avgSpeed * 1.852).toFixed(0), // knots to km/h
    };
  }, [processedItems]);

  // Dados para o gráfico de barras (distância por viagem)
  const chartData = useMemo(() => {
    if (processedItems.length === 0) return [];
    // Agregar por data
    const byDate = {};
    processedItems.forEach((item) => {
      const date = formatTime(item.startTime, 'date');
      if (!byDate[date]) {
        byDate[date] = { date, distance: 0, trips: 0, fuel: 0 };
      }
      byDate[date].distance += item.distance / 1000;
      byDate[date].trips += 1;
      byDate[date].fuel += item.fuelUsed;
    });
    return Object.values(byDate).slice(-14); // últimos 14 dias
  }, [processedItems]);

  const createMarkers = () => ([
    {
      latitude: selectedItem.startLat,
      longitude: selectedItem.startLon,
      image: 'start-success',
    },
    {
      latitude: selectedItem.endLat,
      longitude: selectedItem.endLon,
      image: 'finish-error',
    },
  ]);

  useEffectAsync(async () => {
    if (selectedItem) {
      const query = new URLSearchParams({
        deviceId: selectedItem.deviceId,
        from: selectedItem.startTime,
        to: selectedItem.endTime,
      });
      const response = await fetchOrThrow(`/api/reports/route?${query.toString()}`, {
        headers: { Accept: 'application/json' },
      });
      setRoute(await response.json());
    } else {
      setRoute(null);
    }
  }, [selectedItem]);

  useEffect(() => {
    const handleAfterPrint = () => {
      dispatch(sessionActions.setPrinting(false));
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [dispatch]);

  useEffect(() => {
    if (printing) {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => nativePostMessage(`print|${t('reportTrips')}.pdf`)),
      );
    }
  }, [printing, t]);

  const loadReport = async ({ deviceIds, groupIds, from, to }) => {
    const query = new URLSearchParams({ from, to });
    deviceIds.forEach((deviceId) => query.append('deviceId', deviceId));
    groupIds.forEach((groupId) => query.append('groupId', groupId));
    setLoading(true);
    try {
      const response = await fetchOrThrow(`/api/reports/trips?${query.toString()}`, {
        headers: { Accept: 'application/json' },
      });
      const data = await response.json();
      setItems(data);
      setHasResults(data.length > 0);
    } finally {
      setLoading(false);
    }
  };

  const onShow = useCatch(async ({ deviceIds, groupIds, from, to }) => {
    setSelectedDeviceIds(deviceIds);
    setFromDate(from || '');
    setToDate(to || '');
    await loadReport({ deviceIds, groupIds, from, to });
  });

  const onExport = useCatch(async ({ deviceIds, groupIds, from, to }) => {
    const query = new URLSearchParams({ from, to, fuelPrice });
    deviceIds.forEach((id) => query.append('deviceId', id));
    groupIds.forEach((id) => query.append('groupId', id));
    await downloadReportXlsx('trips', query, `${t('reportTrips')}.xlsx`);
  });

  const onSchedule = useCatch(async (deviceIds, groupIds, report) => {
    report.type = 'trips';
    await scheduleReport(deviceIds, groupIds, report);
    navigate('/reports/scheduled');
  });

  const onPrint = useCatch(async ({ deviceIds = [], groupIds = [], from, to } = {}) => {
    const effectiveDeviceIds = deviceIds.length ? deviceIds : selectedDeviceIds;
    const effectiveFrom = from ?? fromDate;
    const effectiveTo = to ?? toDate;

    if (!effectiveDeviceIds.length || !effectiveFrom || !effectiveTo) {
      return;
    }

    if (!processedItems.length) {
      await loadReport({
        deviceIds: effectiveDeviceIds,
        groupIds,
        from: effectiveFrom,
        to: effectiveTo,
      });
    }

    setSelectedDeviceIds(effectiveDeviceIds);
    setFromDate(effectiveFrom);
    setToDate(effectiveTo);
    dispatch(sessionActions.setPrinting(true));
  });

  const navigateToReplay = (item) => {
    navigate({
      pathname: '/replay',
      search: new URLSearchParams({
        from: item.startTime,
        to: item.endTime,
        deviceId: item.deviceId,
      }).toString(),
    });
  };

  const formatValue = (item, key) => {
    const value = item[key];
    switch (key) {
      case 'deviceId':
        return devices[value].name;
      case 'startTime':
      case 'endTime':
        return formatTime(value, 'minutes');
      case 'startOdometer':
      case 'endOdometer':
      case 'distance':
        return formatDistance(value, distanceUnit, t);
      case 'averageSpeed':
      case 'maxSpeed':
        return value > 0 ? formatSpeed(value, speedUnit, t) : null;
      case 'duration':
        return formatNumericHours(value, t);
      case 'spentFuel':
        if (item.fuelUsed > 0) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {formatVolume(item.fuelUsed, volumeUnit, t)}
              {item.fuelEstimated && <Chip label="est" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />}
            </Box>
          );
        }
        return null;
      case 'fuelCost':
        return item.fuelCost > 0 ? `R$ ${item.fuelCost.toFixed(2)}` : null;
      case 'startAddress':
        return (
          <PoiLocationValue
            latitude={item.startLat}
            longitude={item.startLon}
            poiId={item.startPoiId}
            poiName={item.startPoiName}
            originalAddress={value}
          />
        );
      case 'endAddress':
        return (
          <PoiLocationValue
            latitude={item.endLat}
            longitude={item.endLon}
            poiId={item.endPoiId}
            poiName={item.endPoiName}
            originalAddress={value}
          />
        );
      default:
        return value;
    }
  };

  const deviceNames = selectedDeviceIds
    .map((deviceId) => devices[deviceId]?.name || devices[deviceId]?.uniqueId || `${deviceId}`)
    .filter(Boolean)
    .join(', ');

  // Valores formatados para impressão (texto simples, sem JSX)
  const getPrintableValue = (item, key) => {
    const value = item[key];
    switch (key) {
      case 'deviceId':
        return devices[value]?.name || value;
      case 'startTime':
      case 'endTime':
        return formatTime(value, 'minutes');
      case 'startOdometer':
      case 'endOdometer':
      case 'distance':
        return formatDistance(value, distanceUnit, t);
      case 'averageSpeed':
      case 'maxSpeed':
        return value > 0 ? formatSpeed(value, speedUnit, t) : '';
      case 'duration':
        return formatNumericHours(value, t);
      case 'spentFuel':
        if (item.fuelUsed > 0) {
          return `${formatVolume(item.fuelUsed, volumeUnit, t)}${item.fuelEstimated ? ' (est)' : ''}`;
        }
        return '';
      case 'fuelCost':
        return item.fuelCost > 0 ? `R$ ${item.fuelCost.toFixed(2)}` : '';
      case 'startAddress':
        return item.startPoiName || value || '';
      case 'endAddress':
        return item.endPoiName || value || '';
      default:
        return value ?? '';
    }
  };

  if (printing) {
    return (
      <PrintLayout
        title={t('reportTrips')}
        from={fromDate}
        to={toDate}
        deviceName={deviceNames}
        onReady={() => window.print()}
      >
        <Table className="print-table">
          <TableHead>
            <TableRow>
              <TableCell>{t('sharedDevice')}</TableCell>
              {columns.map((key) => (
                <TableCell key={key}>{t(columnsMap.get(key))}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {processedItems.map((item) => (
              <TableRow key={`print-${item.startPositionId}`}>
                <TableCell>{devices[item.deviceId]?.name || item.deviceId}</TableCell>
                {columns.map((key) => (
                  <TableCell key={key}>{getPrintableValue(item, key)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </PrintLayout>
    );
  }

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={['reportTitle', 'reportTrips']}>
      <div className={classes.container}>
        {selectedItem && (
          <div className={classes.containerMap}>
            <MapView>
              <MapGeofence />
              {route && (
                <>
                  <MapRoutePath positions={route} />
                  <MapMarkers markers={createMarkers()} />
                  <MapCamera positions={route} />
                </>
              )}
            </MapView>
            <MapScale />
          </div>
        )}
        <div className={classes.containerMain}>
          <div className={classes.header}>
            <ReportFilter
              onShow={onShow}
              onExport={onExport}
              onPrint={onPrint}
              onSchedule={onSchedule}
              deviceType="multiple"
              loading={loading}
              hasResults={hasResults}
            >
              <ColumnSelect columns={columns} setColumns={setColumns} columnsArray={columnsArray} />
              <div className={classes.filterItemSmall}>
                <TextField
                  size="small"
                  type="number"
                  label="R$/L"
                  value={fuelPrice}
                  onChange={(e) => setFuelPrice(Number(e.target.value) || 0)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LocalGasStationIcon fontSize="small" /></InputAdornment>,
                  }}
                  inputProps={{ step: 0.01, min: 0 }}
                  fullWidth
                />
              </div>
            </ReportFilter>
          </div>
          {/* Premium Stats Cards */}
          {stats && (
            <Box sx={{ px: 2, py: 2, borderTop: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}` }}>
              <StatCardGrid columns={4} gap={2}>
                <StatCard
                  title={t('reportTrips')}
                  value={stats.trips}
                  icon={<DirectionsCarIcon />}
                  color="primary"
                  size="sm"
                  subtitle={`${stats.totalDuration}h ${t('reportDuration')}`}
                />
                <StatCard
                  title={t('sharedDistance')}
                  value={`${stats.totalDistance} km`}
                  icon={<RouteIcon />}
                  color="info"
                  size="sm"
                  subtitle={`${stats.avgSpeed} km/h ${t('sharedAverage')}`}
                />
                <StatCard
                  title={t('reportSpentFuel')}
                  value={`${stats.totalFuel} L`}
                  icon={<LocalGasStationIcon />}
                  color="warning"
                  size="sm"
                />
                <StatCard
                  title={t('reportFuelCost')}
                  value={`R$ ${stats.totalFuelCost}`}
                  icon={<AttachMoneyIcon />}
                  color="success"
                  size="sm"
                />
              </StatCardGrid>

              {/* Chart Toggle */}
              {chartData.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Box
                    onClick={() => setShowChart(!showChart)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      color: tokens?.text?.secondary || '#8b949e',
                      '&:hover': { color: tokens?.accent?.primary || '#00d4aa' },
                    }}
                  >
                    <TimelineIcon fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {t('reportChart') || 'Gráfico'}
                    </Typography>
                    {showChart ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </Box>
                  <Collapse in={showChart}>
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: tokens?.surface?.elevated || '#161b22',
                        borderRadius: tokens?.radius?.lg || '12px',
                        border: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 2, color: tokens?.text?.secondary || '#8b949e' }}
                      >
                        {t('sharedDistance')} {t('reportDaily') || 'por dia'}
                      </Typography>
                      <PremiumBarChart
                        data={chartData}
                        xKey="date"
                        bars={[
                          { dataKey: 'distance', name: 'km', color: tokens?.accent?.primary || '#00d4aa' },
                        ]}
                        height={200}
                        showLegend={false}
                        tooltipFormatter={(value) => `${value.toFixed(1)} km`}
                      />
                    </Box>
                  </Collapse>
                </Box>
              )}
            </Box>
          )}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.columnAction} />
                <TableCell>{t('sharedDevice')}</TableCell>
                {columns.map((key) => (<TableCell key={key}>{t(columnsMap.get(key))}</TableCell>))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading ? processedItems.map((item) => (
                <TableRow key={item.startPositionId}>
                  <TableCell className={classes.columnAction} padding="none">
                    <div className={classes.columnActionContainer}>
                      {selectedItem === item ? (
                        <IconButton size="small" onClick={() => setSelectedItem(null)}>
                          <GpsFixedIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <IconButton size="small" onClick={() => setSelectedItem(item)}>
                          <LocationSearchingIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton size="small" onClick={() => navigateToReplay(item)}>
                        <RouteIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </TableCell>
                  <TableCell>{devices[item.deviceId]?.name}</TableCell>
                  {columns.map((key) => (
                    <TableCell key={key}>
                      {formatValue(item, key)}
                    </TableCell>
                  ))}
                </TableRow>
              )) : (<TableShimmer columns={columns.length + 2} startAction />)}
            </TableBody>
          </Table>
        </div>
      </div>
    </PageLayout>
  );
};

export default TripReportPage;
